import type { ModuleResource } from './lang'

const DATABASE_NAME = 'dragon-language-cache'
const DATABASE_VERSION = 1
const STORE_NAME = 'language_modules'
const OPEN_TIMEOUT = 300

export interface CachedLanguageModule {
  module: string
  version: string
  resources: ModuleResource
}

/**
 * 只读访问主应用写入的语言缓存。无法访问同一运行域时返回 null，由调用方走事件回退。
 */
export class LangCacheReader {
  private databasePromise?: Promise<IDBDatabase | null>
  private disabled = false

  async get(module: string): Promise<CachedLanguageModule | null> {
    const database = await this.getDatabase()
    if (!database) return null

    try {
      return await new Promise<CachedLanguageModule | null>(
        (resolve, reject) => {
          const request = database
            .transaction(STORE_NAME, 'readonly')
            .objectStore(STORE_NAME)
            .get(module)
          request.onsuccess = () => resolve(request.result || null)
          request.onerror = () => reject(request.error)
        },
      )
    } catch {
      this.disable(database)
      return null
    }
  }

  private getDatabase(): Promise<IDBDatabase | null> {
    if (this.disabled) return Promise.resolve(null)
    if (!this.databasePromise) this.databasePromise = this.openDatabase()
    return this.databasePromise
  }

  private openDatabase(): Promise<IDBDatabase | null> {
    if (typeof window === 'undefined' || !window.indexedDB) {
      this.disabled = true
      return Promise.resolve(null)
    }

    return new Promise((resolve) => {
      let settled = false
      let timeoutId: number | undefined

      const settle = (database: IDBDatabase | null) => {
        if (settled) {
          database?.close()
          return
        }
        settled = true
        if (timeoutId !== undefined) window.clearTimeout(timeoutId)
        resolve(database)
      }

      timeoutId = window.setTimeout(() => {
        this.disabled = true
        settle(null)
      }, OPEN_TIMEOUT)

      try {
        const request = window.indexedDB.open(DATABASE_NAME, DATABASE_VERSION)
        request.onsuccess = () => settle(request.result)
        request.onerror = () => {
          this.disabled = true
          settle(null)
        }
        request.onblocked = () => {
          this.disabled = true
          settle(null)
        }
      } catch {
        this.disabled = true
        settle(null)
      }
    })
  }

  private disable(database?: IDBDatabase) {
    this.disabled = true
    database?.close()
  }
}
